import React, { useEffect, useRef } from "react";
import { Button, Flex, Text, VStack } from "@chakra-ui/react";
import { PencilIcon } from "@heroicons/react/outline";
import { DateTime } from "luxon";
import { useMutation, useQuery } from "react-query";
import { useTranslation, Trans } from "react-i18next";
import { identity_instance } from "../../apiConfig";
import Loader from "../../common/Loader";
import ErrorMsg from "../ErrorMsg";
import { UserContext } from "../../context";
import { useApiUser } from "../../context/ApiAuthContext";
import { appInsights } from "../AppInsightsErrorBoundary/AppInsights";

const getPathDir = (urlString) => {
  if (!urlString) return urlString;
  const url = new URL(urlString);
  return url.pathname.match(/([^/]*)\/*$/)[1];
};
const Document = ({ document, onSuccess, onError, onCancel }) => {
  const { t } = useTranslation();
  const authUser = useApiUser();
  const iframeRef = useRef();
  const { userData } = React.useContext(UserContext);
  const documentSigningUrl = document?.metaData?.UrlDocumentSigning?.data;
  const documentUrl = document?.metaData?.UrlDocument?.data;
  const documentType = document?.metaData?.DocumentType?.data;
  const documentRevisionId = document?.metaData?.DocumentRevisionId?.data;
  const documentId = getPathDir(documentUrl);
  let subCategory = "Events";
  if (userData?.event?.children && userData?.event?.children.length > 0) {
    subCategory = "ElementTree";
  }
  const eventName = t(`${subCategory}.${userData?.event?.name}`, {
    ns: subCategory,
  });
  const eventTime = DateTime.fromISO(userData?.lock?.startTime).toFormat("t");
  if (!documentUrl) {
    onError(document?.validationReason);
  }

  const replacements = {
    ...userData.user,
    SignDate: new Date().toDateString(),
  };
  function replaceHB(content) {
    const HBregEx = /(?:\{{2,3}([^{}]+(?=\}{2,3}))\}{2,3})/g;
    const newContent = content.replace(HBregEx, (match, p1) => {
      const replacement = replacements[p1];
      return replacement || p1;
    });
    return newContent;
  }
  const documentQuery = useQuery(["document", documentId], () =>
    identity_instance.get(documentUrl)
  );
  const { mutate, isLoading, isError, error } = useMutation(
    (data) =>
      identity_instance.put(documentSigningUrl, {
        TenantIdOfSigning: authUser.tenantId,
        documentRevisionId: documentRevisionId,
        createDate: DateTime.utc(),
        documentData: data?.documentData
          ? {
              mediaType: "application/json",
              data: data?.documentData,
            }
          : undefined,
      }),
    {
      onSuccess: onSuccess,
      onError: (res) => onError(res.response.data),
    }
  );
  const onSign = () => {
    if (documentType === "QuestionWeighted") {
      try {
        iframeRef.current?.contentWindow?.postMessage("submit", "*");
      } catch (e) {
        appInsights.trackException({
          exception: new Error(e),
          properties: { event: "onSign", document: document },
        });
      }
    } else {
      mutate();
    }
  };
  const onIframeMsg = (event) => {
    const msg = event?.data;
    if (msg.type === "post") {
      mutate({
        documentData: `data:application/json;base64,${btoa(
          JSON.stringify(msg.data)
        )}`,
      });
    }
  };

  useEffect(() => {
    window.addEventListener("message", onIframeMsg);
    return () => {
      window.removeEventListener("message", onIframeMsg);
    };
  }, []);

  return (
    <VStack
      direction={"column"}
      alignItems={"start"}
      flex={1}
      minHeight={0}
      h={"full"}
      spacing={5}
      pr={4}
    >
      <Text
        as={"h2"}
        color="brand.secondary.500"
        fontSize="2xl"
        textAlign={"left"}
        fontWeight={"bold"}
      >
        <Trans
          i18nKey={"new_appointment_event_time"}
          values={{ eventName: eventName, dateTime: eventTime }}
        >
          New Appointment: {userData?.event?.name} @{" "}
          {DateTime.fromISO(userData?.lock?.startTime).toFormat("t")}
        </Trans>
      </Text>
      <Text as="h3" fontSize="xl" textAlign={"left"} fontWeight={"bold"}>
        <Trans i18nKey={"please_review_document"}>
          Please review the document below and click <i>&quot;Sign&quot;</i> to
          agree and continue.
        </Trans>
      </Text>

      <Flex
        overflowY={"auto"}
        w={"full"}
        pt={[0, 10, 10, 10]}
        pb={[0, 0, 0, 0]}
        direction={"column"}
        h={"full"}
        mt={0}
      >
        <Flex
          p={2}
          rounded={"lg"}
          bg={"white"}
          h={"full"}
          w={"full"}
          mb={10}
          position={"relative"}
          color={"black"}
        >
          {(documentQuery.isLoading || isLoading) && <Loader />}
          {documentQuery.isError && (
            <ErrorMsg
              message={t(
                "error.document_get",
                "Error retrieving required document, please see a staff member for assistance."
              )}
            />
          )}
          {isError && (
            <ErrorMsg
              message={t(
                "error.document_sign",
                "Error signing required document, please see a staff member for assistance."
              )}
            />
          )}
          {documentQuery.data && (
            <iframe
              ref={iframeRef}
              srcDoc={replaceHB(documentQuery?.data?.data)}
              width="100%"
              height="100%"
            />
          )}
        </Flex>
        <Flex justifyContent="flex-end" pb={10} flex={1} w={"full"}>
          <Button
            size={"lg"}
            onClick={onCancel}
            mr={5}
            data-testid={"document-cancel-button"}
          >
            <Trans i18nKey={"actions.cancel"}>Cancel</Trans>
          </Button>

          <Button
            size={"lg"}
            onClick={onSign}
            leftIcon={<PencilIcon height={12} />}
            variant="kioskPrimary"
            isLoading={isLoading}
            loadingText={t("loader.signing", "Signing...")}
            disabled={isLoading}
            data-testid={"document-sign-button"}
          >
            <Trans i18nKey={"actions.sign"}>Sign</Trans>
          </Button>
        </Flex>
      </Flex>
    </VStack>
  );
};

export default Document;
